import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { DEITY_CATEGORIES } from "../Deities";

export function FestivalsAdminTab() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState({});
  const load = () => api.get("/festivals").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const save = async (f) => {
    const date = edit[f.slug];
    try {
      await api.put(`/admin/festivals/${f.slug}`, { name: f.name, hindi: f.hindi, date, region: f.region, deities: f.deities, significance: f.significance, rituals: f.rituals });
      toast.success(`${f.name} → ${date}`); setEdit({ ...edit, [f.slug]: undefined }); load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const remind = async (f, test) => {
    const test_email = test ? window.prompt("Send a test reminder to which email?") : null;
    if (test && !test_email) return;
    if (!test && !window.confirm(`Email all newsletter subscribers about ${f.name} now?`)) return;
    try { const { data } = await api.post(`/admin/festivals/${f.slug}/remind`, { test_email }); toast.success(test ? "Test email sent" : `Sending to ${data.subscribers} subscribers`); }
    catch (e) { toast.error(formatError(e)); }
  };
  const remindFollowers = async (f) => {
    const test_email = window.prompt("Send a sample follower alert to which email?");
    if (!test_email) return;
    try { await api.post(`/admin/festivals/${f.slug}/remind-followers`, { test_email }); toast.success("Follower test email sent"); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="festivals-admin-tab" className="space-y-6">
      <div className="bg-[#F3E5AB]/40 border border-[#D4AF37]/40 rounded-2xl p-5 text-sm text-[#4A0E17]">
        Reminder emails with a seva link go to all newsletter subscribers automatically, <b>3 days before every festival</b> (daily check at 7:00 AM IST). People who follow a deity also get a personal alert for that deity's festivals, with an unsubscribe link. You can also send one manually below.
      </div>
      <NewFestival onSaved={load} />
      <div className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>{["Festival", "Region", "Date", "Actions"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.slug} className="border-t border-[#D4AF37]/20">
                <td className="px-4 py-2 font-semibold">{f.name}{f.estimated && <span className="ml-2 text-[10px] text-[#8C6D3B]">(estimated, please confirm)</span>}</td>
                <td className="px-4 py-2">{f.region}</td>
                <td className="px-4 py-2"><input data-testid={`fest-date-${f.slug}`} type="date" value={edit[f.slug] ?? f.date} onChange={(e) => setEdit({ ...edit, [f.slug]: e.target.value })} className="border border-[#D4AF37]/40 rounded px-2 h-9" /></td>
                <td className="px-4 py-2"><div className="flex gap-2 items-center">
                  {edit[f.slug] && edit[f.slug] !== f.date && <Button data-testid={`fest-save-${f.slug}`} onClick={() => save(f)} className="h-8 text-xs rounded-full bg-[#4A0E17]">Save</Button>}
                  <button data-testid={`fest-test-${f.slug}`} onClick={() => remind(f, true)} className="text-xs text-[#8C6D3B] underline">Test email</button>
                  <button data-testid={`fest-remind-${f.slug}`} onClick={() => remind(f, false)} className="text-xs text-[#E65100] underline">Send now</button>
                  <button data-testid={`fest-follow-test-${f.slug}`} onClick={() => remindFollowers(f)} className="text-xs text-[#4A0E17] underline">Test follower email</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewFestival({ onSaved }) {
  const blank = { name: "", hindi: "", date: "", region: "Pan-India", deities: "", significance: "", rituals: "" };
  const [f, setF] = useState(blank);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/festivals", { ...f, deities: f.deities.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Festival added"); setF(blank); onSaved(); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <form onSubmit={save} data-testid="new-festival-form" className="bg-[#FFFDF9] sacred-border rounded-2xl p-6 grid sm:grid-cols-3 gap-3">
      <div className="sm:col-span-3 font-serif text-xl text-[#4A0E17]">Add a festival</div>
      <Input data-testid="nf-name" required placeholder="Name" value={f.name} onChange={set("name")} className="bg-white h-10" />
      <Input data-testid="nf-date" required type="date" value={f.date} onChange={set("date")} className="bg-white h-10" />
      <Input data-testid="nf-region" placeholder="Region" value={f.region} onChange={set("region")} className="bg-white h-10" />
      <Input data-testid="nf-deities" placeholder="Deity slugs, comma separated (e.g. shiva, parvati)" value={f.deities} onChange={set("deities")} className="bg-white h-10 sm:col-span-2" />
      <Input placeholder="Hindi name" value={f.hindi} onChange={set("hindi")} className="bg-white h-10" />
      <Input placeholder="Significance" value={f.significance} onChange={set("significance")} className="bg-white h-10 sm:col-span-2" />
      <Button data-testid="nf-submit" className="bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-10">Add festival</Button>
    </form>
  );
}

export function DeitiesAdminTab() {
  const blank = { name: "", sanskrit: "", category: "Regional", title: "", mantra: "", mantra_meaning: "", story: "", vahana: "", consort: "", symbols: "", temples: "", image_url: "" };
  const [f, setF] = useState(blank);
  const [items, setItems] = useState([]);
  const load = () => api.get("/deities").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/uploads/image", fd); setF((x) => ({ ...x, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` })); toast.success("Image uploaded"); }
    catch (err) { toast.error(formatError(err)); }
  };
  const save = async (e) => {
    e.preventDefault();
    const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
    try { await api.post("/admin/deities", { ...f, symbols: split(f.symbols), temples: split(f.temples) }); toast.success("Deity added"); setF(blank); load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const del = async (slug) => { if (!window.confirm("Delete this deity page?")) return; await api.delete(`/admin/deities/${slug}`); load(); };
  return (
    <div data-testid="deities-admin-tab" className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={save} className="bg-[#FFFDF9] sacred-border rounded-2xl p-6 space-y-3">
        <div className="font-serif text-xl text-[#4A0E17]">Add a deity</div>
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="nd-name" required placeholder="Name (e.g. Lord Murugan)" value={f.name} onChange={set("name")} className="bg-white h-10" />
          <Input data-testid="nd-sanskrit" placeholder="Sanskrit name" value={f.sanskrit} onChange={set("sanskrit")} className="bg-white h-10" />
          <select data-testid="nd-category" value={f.category} onChange={set("category")} className="h-10 rounded-md border border-[#D4AF37]/40 bg-white px-2 text-sm">{DEITY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
          <Input placeholder="Title / epithet" value={f.title} onChange={set("title")} className="bg-white h-10" />
        </div>
        <Input data-testid="nd-mantra" placeholder="Mantra" value={f.mantra} onChange={set("mantra")} className="bg-white h-10" />
        <Input placeholder="Mantra meaning" value={f.mantra_meaning} onChange={set("mantra_meaning")} className="bg-white h-10" />
        <Textarea data-testid="nd-story" rows={4} placeholder="Story & significance" value={f.story} onChange={set("story")} className="bg-white" />
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Vahana" value={f.vahana} onChange={set("vahana")} className="bg-white h-10" />
          <Input placeholder="Consort" value={f.consort} onChange={set("consort")} className="bg-white h-10" />
        </div>
        <Input placeholder="Symbols, comma separated" value={f.symbols} onChange={set("symbols")} className="bg-white h-10" />
        <Input placeholder="Temples, comma separated" value={f.temples} onChange={set("temples")} className="bg-white h-10" />
        <div className="flex items-center gap-3">{f.image_url && <img src={f.image_url} alt="" className="w-12 h-12 rounded object-cover" />}<input data-testid="nd-image" type="file" accept="image/*" onChange={upload} className="text-xs" /></div>
        <Button data-testid="nd-submit" className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-10">Publish deity page</Button>
      </form>
      <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6 max-h-[640px] overflow-y-auto">
        <div className="font-serif text-xl text-[#4A0E17] mb-3">{items.length} deity pages</div>
        {items.map((d) => (
          <div key={d.slug} className="flex items-center gap-3 py-2 border-b border-[#D4AF37]/20">
            <img src={d.image_url} alt="" className="w-9 h-9 rounded-full object-cover" />
            <div className="flex-1 text-sm"><div className="font-semibold">{d.name}</div><div className="text-xs text-[#63534E]">{d.category}</div></div>
            <button data-testid={`del-deity-${d.slug}`} onClick={() => del(d.slug)} className="text-xs text-red-700">Delete</button>
          </div>
        ))}
      </div>
      <DeityTracksAdmin deities={items} />
    </div>
  );
}

function DeityTracksAdmin({ deities }) {
  const [slug, setSlug] = useState("shiva");
  const [tracks, setTracks] = useState([]);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("aarti");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/deities/${slug}`).then((r) => setTracks(r.data.tracks || []));
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose an audio file");
    const fd = new FormData(); fd.append("file", file); fd.append("title", title); fd.append("kind", kind);
    setBusy(true);
    try { await api.post(`/admin/deities/${slug}/tracks`, fd); toast.success("Track uploaded"); setTitle(""); setFile(null); e.target.reset(); load(); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const act = async (fn) => { try { await fn(); load(); } catch (err) { toast.error(formatError(err)); } };
  return (
    <div data-testid="deity-tracks-admin" className="lg:col-span-2 bg-[#FFFDF9] sacred-border rounded-2xl p-6">
      <div className="font-serif text-xl text-[#4A0E17]">Bhajan & aarti audio</div>
      <p className="text-sm text-[#63534E] mt-1">Upload several recordings per deity. The <b>first track</b> plays when devotees press the Chant button.</p>
      <div className="mt-4 grid lg:grid-cols-2 gap-6">
        <form onSubmit={add} className="space-y-3">
          <select data-testid="tracks-deity" value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full h-10 rounded-md border border-[#D4AF37]/40 bg-white px-2 text-sm">
            {deities.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
          </select>
          <div className="grid grid-cols-3 gap-3">
            <Input data-testid="track-title" required minLength={2} placeholder="Title (e.g. Om Jai Shiv Omkara)" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-white h-10 col-span-2" />
            <select data-testid="track-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="h-10 rounded-md border border-[#D4AF37]/40 bg-white px-2 text-sm">
              {["aarti", "bhajan", "mantra", "stotram", "chalisa"].map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <input data-testid="track-file" type="file" accept="audio/*" onChange={(e) => setFile(e.target.files?.[0])} className="text-xs" />
          <Button data-testid="track-submit" disabled={busy} className="w-full bg-[#E65100] hover:bg-[#BF360C] rounded-full h-10">{busy ? "Uploading…" : "Upload track (max 25MB)"}</Button>
        </form>
        <div className="space-y-2">
          {tracks.length === 0 && <div className="text-sm text-[#63534E]">No tracks yet. The chant button reads the mantra aloud instead.</div>}
          {tracks.map((t, i) => (
            <div key={t.id} data-testid={`admin-track-${t.id}`} className="flex items-center gap-3 border border-[#D4AF37]/30 rounded-xl p-3 bg-white">
              <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{i === 0 && <span className="text-[#E65100]">★ </span>}{t.title}</div><div className="text-xs text-[#63534E] capitalize">{t.kind}</div>
                <audio controls preload="none" src={`${process.env.REACT_APP_BACKEND_URL}${t.audio_url}`} className="w-full h-8 mt-1" /></div>
              {i > 0 && <button data-testid={`track-first-${t.id}`} onClick={() => act(() => api.put(`/admin/deities/${slug}/tracks/${t.id}/first`))} className="text-xs text-[#4A0E17] underline">Make first</button>}
              <button data-testid={`track-del-${t.id}`} onClick={() => act(() => api.delete(`/admin/deities/${slug}/tracks/${t.id}`))} className="text-xs text-red-700">Delete</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
